// The navigational lifecycle's CASE-POLICY GATE and the database name every
// key downstream is computed from.
//
// Nothing in the area may be keyed before both halves of the case policy are
// in — the connection's (MySQL's `@@lower_case_table_names`, SQL Server's
// server collation) and then each DATABASE's (SQL Server's per-database
// collation) — because an index keyed on a guess cannot be repaired
// (`navigationalLifecycle.ts` holds the full argument). This module is that
// gate: `ensureFullCasePolicyKnown` runs the two probes in order and hands back
// the database NAME everything downstream keys on, which for a connection
// configured with NO database is the login default's real name the first probe
// learns on the way past (`rememberDefaultDatabase`).
//
// Imports the registry (`navLifecycleRegistry.ts`) for the entry shape and the
// liveness check; imports nothing else from the area, so the build
// (`navLifecycleBuild.ts`) and the facade can both sit on top of it.

import type { DatabaseEngineId } from '@/core/engines';

import { asString, fetchRows } from '../index/engines/queryHelper';

import { databaseIdentityKey } from './databaseIdentity';
import { asNavIndexUnavailable, navIndexUnavailable } from './navIndexUnavailable';
import { assertStillLive, liveLifecycle, type ConnectionEntry } from './navLifecycleRegistry';

/**
 * One row on the introspection lane, exactly like the case-policy probes it
 * rides along with — the name the session is ALREADY attached to, asked for on
 * the engines where leaving it unknown costs something.
 *
 * - `mssql` / `pgsql` — a database NAME is part of the index identity there,
 *   so an unresolved `''` files the login default a SECOND time under its own
 *   spelling ({@link rememberDefaultDatabase} has the full argument).
 * - `oracle` — one identity per connection, so nothing is disambiguated; the
 *   name is paid for because it is the query-ROUTING value (the adapter quotes
 *   it into `ALTER SESSION SET CURRENT_SCHEMA`) and the name every reader
 *   compares by ({@link resolvedDatabaseFor}), so `''` against the tree's
 *   `JAMSQL` opens two References tabs over the one index. The alias is
 *   QUOTED lower-case because Oracle upper-cases an undelimited one, which the
 *   `rows[0]?.v` read below would then miss — the same shape every Oracle
 *   catalog feed uses (`index/engines/oracleObjectBodyFetcher.ts`).
 *
 *   `SESSION_USER`, not `CURRENT_SCHEMA`. The probe below routes with `''`, and
 *   the adapter turns that into an UNTAGGED pool checkout: it runs no
 *   `ALTER SESSION` of its own, and the tag shim's untagged checkout only
 *   FORGETS the tag it was tracking — it never restores the session's schema
 *   (`electron/oracle-connection/oracleSessionTags.ts`, which lists the modules
 *   that move `CURRENT_SCHEMA` on a pooled session: schema compare, clone,
 *   dependency lookup, notebook sessions, import). So `CURRENT_SCHEMA` on a
 *   `''` checkout is whatever the LAST holder of that session left it at, which
 *   is not a property of this connection at all. `SESSION_USER` is the
 *   authenticated login, unmoved by `ALTER SESSION SET CURRENT_SCHEMA`
 *   (verified live: after `ALTER SESSION SET CURRENT_SCHEMA = "SYS"`,
 *   `CURRENT_SCHEMA` reads `SYS` while `SESSION_USER` still reads `JAMSQL`) —
 *   and the login's own schema is exactly what this probe is for, since
 *   {@link rememberDefaultDatabase} only runs when NO `oracleInitialSchema` is
 *   configured, i.e. when nothing has moved the session on purpose.
 * - `mysql` / `sqlite` — one identity AND a routing value that means nothing
 *   to the catalog feed (`information_schema` / one file), so no probe.
 * - `kusto` / `mock` — key nothing of their own.
 */
const DEFAULT_DATABASE_SQL: Partial<Record<DatabaseEngineId, string>> = {
  mssql: 'SELECT DB_NAME() AS v',
  pgsql: 'SELECT current_database() AS v',
  oracle: `SELECT SYS_CONTEXT('USERENV', 'SESSION_USER') AS "v" FROM DUAL`,
};

/** The engines {@link DEFAULT_DATABASE_SQL} has a statement for — derived, so
 *  the gate and the statement cannot drift apart. */
const RESOLVES_DEFAULT_DATABASE = new Set<DatabaseEngineId>(
  Object.keys(DEFAULT_DATABASE_SQL) as DatabaseEngineId[],
);

export async function probeDefaultDatabase(
  connectionId: string,
  engineId: DatabaseEngineId,
): Promise<string | undefined> {
  const sql = DEFAULT_DATABASE_SQL[engineId];
  if (!sql) return undefined;
  // `''` as the routing database is the point: it is what the connection has
  // been using all along, so the row comes back from the database the login
  // actually landed in — and on Oracle from the owner the session sits in,
  // with no `ALTER SESSION` of our own in front of it.
  const rows = await fetchRows(connectionId, '', sql, 'nav-default-database');
  return asString(rows[0]?.v) || undefined;
}

/** The fields of a store connection this module reads. Structural rather than
 *  the whole `Connection`, so the resolution stays unit-testable without the
 *  renderer's connection model. */
export type LifecycleDatabaseSource = {
  readonly database?: string;
  readonly oracleInitialSchema?: string;
};

/**
 * The connection's own database as the NAVIGATIONAL lifecycle means it: the
 * value its catalog queries ROUTE with, and the value every key downstream
 * resolves from when a request names none.
 *
 * It is `connection.database` on every engine but ORACLE, where that field
 * cannot be read as a database name at all. It is the SERVICE-name slot: the
 * connect string dials `oracleServiceName || database`
 * (`electron/oracle-connection/oracleConnectString.ts`), so a record carrying
 * the service ONLY there connects perfectly well and arrives here with
 * `database: 'FREEPDB1'` — a legacy saved connection from before
 * `oracleServiceName` existed, or a DBeaver import, whose mapper passes
 * `configuration.database` straight through for every engine with no Oracle
 * clearing (`electron/connection-import/sources/dbeaverMapper.ts`) and sets no
 * `oracleInitialSchema` at all. And when it does hold an owner instead (the
 * dialog shows a free-text Database field for Oracle, and its "Load databases"
 * list is `ALL_USERS`), it still is not the owner the SESSION is in.
 *
 * An Oracle "database" in this area is an OWNER — the Object Explorer and the
 * database dropdown both list `ALL_USERS` — and the entry's database is quoted
 * verbatim into `ALTER SESSION SET CURRENT_SCHEMA = "…"` by the adapter
 * (`electron/query-engine-adapters/oracleAdapter.ts`). A service name there
 * fails every statement of the build with ORA-01435, and because Oracle keys
 * ONE identity per connection (`databaseIdentityKey` answers `''` for every
 * owner) the failed entry is the entry a later explicit `JAMSQL` request finds
 * and RETRIES — with the same service name, forever.
 *
 * Discarding a `database` that IS an owner costs nothing: Oracle's catalog
 * feeds read `ALL_*` with an explicit `OWNER` filter and enumerate every owner
 * the login can see whatever `CURRENT_SCHEMA` is, so the index is the same
 * either way — while an unnamed request (`resolvedDatabaseFor(cid, '')`) then
 * answers the owner such a request actually RUNS in, which is the session's,
 * not the dialog's.
 *
 * What the session is actually attached to is:
 *   - `oracleInitialSchema` when one is configured — the pool runs that same
 *     `ALTER SESSION` on every new session and REFUSES the connection when the
 *     name fails identifier validation (`oraclePoolFactory.ts`), so a
 *     connection that reached `'connected'` is sitting in exactly that owner
 *     and the value needs no re-validation here;
 *   - the login's own schema otherwise — `''` issues no `ALTER SESSION` of
 *     ours, so the session stays where it is, and {@link rememberDefaultDatabase}
 *     learns that login's NAME from `SESSION_USER` (see
 *     {@link DEFAULT_DATABASE_SQL} for why not `CURRENT_SCHEMA`, which on a
 *     pooled session is whoever held it last).
 *
 * Same reading as `database-objects/services/preloadPriority.ts`'s
 * `resolveHomePreloadDatabase`, which reached it first for the Object
 * Explorer's preload order.
 */
export function lifecycleDatabaseOf(
  connection: LifecycleDatabaseSource | undefined,
  engineId: DatabaseEngineId,
): string {
  if (engineId === 'oracle') return connection?.oracleInitialSchema?.trim() ?? '';
  return connection?.database ?? '';
}

/** An empty / omitted database means "the connection's configured one" — and
 *  when nothing was configured, the login default's own NAME once the gate has
 *  learnt it ({@link rememberDefaultDatabase}), so a request that spells that
 *  database out lands on the same identity. Until then (and forever, if the
 *  probe failed) it stays `''`, which routes to the login default. */
export function resolveDatabase(entry: ConnectionEntry, database: string | undefined): string {
  if (database) return database;
  return entry.resolvedDefaultDatabase ?? entry.defaultDatabase;
}

/**
 * The database one index entry's catalog queries actually RUN IN — which is not
 * always the name the request that created it carried.
 *
 * `resolveDatabase` answers "which database is this request ABOUT", and on SQL
 * Server / PostgreSQL that is also the one to run in: the name IS the identity
 * there, and the catalog feeds read the database they are connected to. On
 * MySQL, Oracle and SQLite one index spans the whole connection
 * ({@link databaseIdentityKey} answers `''` for every name), so the name only
 * ROUTES the query — and routing it with the requester's name is how a single
 * bad early request poisons the connection's only entry:
 *
 * The connect-time schedule waits for the engine's full tiered snapshot and
 * then for an idle moment, so a USER GESTURE routinely reaches `indexReadyFor`
 * first and CREATES that shared entry. Several tab-creation paths spell
 * `connection.database` into the tab they open (`quickActions.ts`,
 * `useToolbarFileActions.ts`, `newObjectScriptService.ts` and the object
 * context-menu services), and MCP's `ui_open_tab` takes the database as free
 * text — and on Oracle `connection.database` is the SERVICE-name slot (see
 * {@link lifecycleDatabaseOf}). Find References / Peek / F2 from such a tab
 * therefore files the connection's one entry with `FREEPDB1`, which the adapter
 * quotes into `ALTER SESSION SET CURRENT_SCHEMA = "FREEPDB1"` — ORA-01435,
 * every statement of the build, and because the entry is shared, the explicit
 * `JAMSQL` request that follows RETRIES that same entry and re-reads the same
 * service name, forever. Round 15 closed the connect-time half of this
 * ({@link lifecycleDatabaseOf}); this is the requester half.
 *
 * So the entry routes with the CONNECTION's own database whenever that names
 * the same identity as the request — which on those three engines is always.
 * Two reasons it is the right value rather than merely a safer one:
 *   - It is the one database this connection is KNOWN to be able to route to:
 *     it opened with it. Oracle's pool runs the `ALTER SESSION` for a
 *     configured `oracleInitialSchema` on every new session and refuses the
 *     connection when it fails (`oraclePoolFactory.ts`), and `''` routes to the
 *     login's own schema with no `ALTER SESSION` at all.
 *   - It changes nothing about what the index HOLDS. Oracle's feeds read
 *     `ALL_*` filtered by `OWNER` and MySQL's read `information_schema`, both
 *     enumerating every schema the login can see whatever the session is
 *     attached to (verified live: `ALL_VIEWS WHERE OWNER = 'JAMSQL'` answers
 *     the same under `CURRENT_SCHEMA = SYS`, and MySQL's listing needs no `USE`
 *     at all), and a SQLite connection IS one file — its adapter never reads
 *     the value. It is also the reading the DDL invalidator already documents
 *     for the registration it is handed (`implicitSchemaFor`: "the schema the
 *     app scopes the connection to (the login's own)").
 *
 * On SQL Server and PostgreSQL the guard is never taken for a DIFFERENT
 * database, so an on-demand index for `MandTob` still runs in `MandTob`. The
 * guard IS taken when the two names are one identity — a `_CI_` instance's
 * `Sales` and `sales` — where the connection's own spelling is as correct as
 * the request's and they route to the same database.
 */
export function routingDatabaseFor(
  connectionId: string,
  entry: ConnectionEntry,
  database: string,
): string {
  const own = resolveDatabase(entry, undefined);
  if (own === database) return database;
  const identity = (db: string): string => databaseIdentityKey(connectionId, entry.engineId, db);
  return identity(own) === identity(database) ? own : database;
}

/** {@link resolveDatabase} for a READER that holds only a connection id — the
 *  References tab deciding whether two requests name the same database, which
 *  has to compare the RESOLVED names or a `''` request and a `master` request
 *  open two tabs over the one index they share.
 *
 *  It lives here rather than inside `databaseComparisonKey`
 *  (`databaseIdentity.ts`) because that module is imported BY the lifecycle:
 *  consulting the registry from there would close the cycle the split exists to
 *  prevent. A connection the lifecycle does not hold (or one whose default was
 *  never learnt) answers the request unchanged — the same "wasteful, never
 *  wrong" fallback the resolution itself keeps. */
export function resolvedDatabaseFor(connectionId: string, database?: string): string {
  const entry = liveLifecycle.get(connectionId);
  if (!entry) return database ?? '';
  return resolveDatabase(entry, database);
}

/** Both halves of the case policy for `database`, in order — the connection's
 *  first (its answer decides the database identity the second files under),
 *  then that database's. Hands back the database NAME everything downstream
 *  keys on, which is not always the one asked for: an empty / omitted request
 *  on a connection configured with no database resolves to the login default's
 *  real name, which the first half is where the connection learns.
 *
 *  Rejects when either policy cannot be learnt, and when the entry stops being
 *  the live one while a probe was in flight — `assertStillLive` tells a plain
 *  teardown (`'no-lifecycle'`, nothing to build into) from a
 *  teardown-then-reconnect under the same id (`'cancelled'` — a replacement
 *  entry now lives here, and it may not be built yet either). */
export async function ensureFullCasePolicyKnown(
  connectionId: string,
  entry: ConnectionEntry,
  database: string | undefined,
): Promise<string> {
  await ensureCasePolicyKnown(connectionId, entry, resolveDatabase(entry, database));
  assertStillLive(connectionId, entry);
  // Re-resolved, not carried over: the gate above is also where an unconfigured
  // connection learns the login default's name, so `''` may have become
  // `master` — and the per-DATABASE probe below has to file its answer under
  // the identity the index will actually be keyed by.
  const target = resolveDatabase(entry, database);
  await ensureDatabaseCasePolicyKnown(connectionId, entry, target);
  assertStillLive(connectionId, entry);
  return target;
}

/** The connection's case policy, learnt once and shared by every database. A
 *  failed attempt is NOT remembered: it is dropped so the next request probes
 *  again (no background retry loop — the next user gesture is the retry). */
function ensureCasePolicyKnown(
  connectionId: string,
  entry: ConnectionEntry,
  database: string,
): Promise<void> {
  if (!entry.casePolicy) {
    entry.casePolicy = entry.deps
      .ensureCasePolicy(connectionId, entry.engineId, database)
      .then(async (known) => {
        if (!known) {
          throw navIndexUnavailable(
            'policy-unknown',
            `nav index case policy unknown for connection ${connectionId}`,
          );
        }
        // Rides along with the policy: the same once-per-connection gate, the
        // same introspection lane, and its answer is what the SECOND (per
        // database) probe then keys on. It never rejects — see there.
        await rememberDefaultDatabase(connectionId, entry);
      })
      .catch((err: unknown) => {
        entry.casePolicy = undefined;
        // A probe that threw (a dead IPC bridge, say) is the same answer as one
        // that said "not known": nothing was keyed, and the next request retries.
        throw asNavIndexUnavailable('policy-unknown', err);
      });
  }
  return entry.casePolicy;
}

/**
 * Resolve `''` — "nothing was configured for this connection" — to the name
 * the session is actually attached to, once per connection.
 *
 * On a MULTI-DATABASE engine, without it the same catalog is indexed TWICE:
 * the connect-time schedule files the login default under the identity `''`,
 * while the Object Explorer and every query tab name that database explicitly
 * (`master`) and file a second, identical index under `'master'` — with its own
 * DDL registration, so every DDL refreshes the same catalog twice, and with a
 * References tab that never reuses itself across the two spellings.
 *
 * ORACLE pays for it too, for a different reason: one connection keys ONE
 * identity there, so nothing is disambiguated, but the name is the value the
 * catalog queries ROUTE with and the value every reader compares by
 * ({@link resolvedDatabaseFor}). `''` against the tree's `JAMSQL` opens a
 * second References tab over the one index, headed by an empty database name.
 * It only ever reaches here when no `oracleInitialSchema` is configured —
 * {@link lifecycleDatabaseOf} takes that one verbatim, because the pool has
 * already `ALTER SESSION`ed the connection into it.
 *
 * Only {@link RESOLVES_DEFAULT_DATABASE} pay for it, and only when nothing was
 * configured. A probe that FAILS leaves `''` — the behaviour that stood before,
 * which is wasteful but never wrong, and which on every engine still ROUTES to
 * the session's own database — rather than guessing a name; the next connection
 * is the next chance, since this rides on the once-per-connection case-policy
 * gate. It therefore never rejects: the gate it rides on refuses to key
 * anything when IT fails, and a missing default name is not that kind of
 * failure.
 */
async function rememberDefaultDatabase(connectionId: string, entry: ConnectionEntry): Promise<void> {
  if (entry.defaultDatabase) return;
  if (entry.resolvedDefaultDatabase !== undefined) return;
  if (!RESOLVES_DEFAULT_DATABASE.has(entry.engineId)) return;
  try {
    const resolved = await entry.deps.resolveDefaultDatabase(connectionId, entry.engineId);
    // The connection may have gone away (and the id been reused) while the row
    // was in flight — the same rule the case-policy probes follow.
    if (resolved && liveLifecycle.get(connectionId) === entry) {
      entry.resolvedDefaultDatabase = resolved;
    }
  } catch {
    // Unknown stays unknown.
  }
}

/** The same, per database identity — SQL Server's object and schema names
 *  follow the DATABASE's collation, so one answer per connection cannot cover
 *  them. Every other engine's gate resolves without a query. */
function ensureDatabaseCasePolicyKnown(
  connectionId: string,
  entry: ConnectionEntry,
  database: string,
): Promise<void> {
  const key = databaseIdentityKey(connectionId, entry.engineId, database);
  const pending = entry.databaseCasePolicy.get(key);
  if (pending) return pending;
  const probe = entry.deps
    .ensureDatabaseCasePolicy(connectionId, entry.engineId, database)
    .then((known) => {
      if (known) return;
      throw navIndexUnavailable(
        'policy-unknown',
        `nav index case policy unknown for database ${key || '(default)'}`,
      );
    })
    .catch((err: unknown) => {
      entry.databaseCasePolicy.delete(key);
      throw asNavIndexUnavailable('policy-unknown', err);
    });
  entry.databaseCasePolicy.set(key, probe);
  return probe;
}
